import React, { useState, useEffect } from 'react';
import {
  X,
  MapPin,
  Star,
  Clock,
  Globe,
  Phone,
  DollarSign,
  ExternalLink,
  Navigation,
  Sparkles,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';

const POIDetailModal = ({
  poi,
  isOpen,
  onClose,
  onGetDirections,
  waypoints = [],
  totalStops = 0,
  onTagClick = () => {},
}) => {
  const [placeDetails, setPlaceDetails] = useState(null);
  const [showFullHours, setShowFullHours] = useState(false);

  useEffect(() => {
    if (!isOpen || !poi?.place_id) {
      setPlaceDetails(null);
      return;
    }
    if (!window.google?.maps?.places) return;

    const el = document.createElement('div');
    const service = new window.google.maps.places.PlacesService(el);
    service.getDetails(
      {
        placeId: poi.place_id,
        fields: ['opening_hours', 'formatted_phone_number', 'website', 'url', 'business_status', 'photos'],
      },
      (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
          setPlaceDetails(place);
        }
      }
    );
  }, [isOpen, poi?.place_id]);

  if (!isOpen || !poi) return null;

  const isRegularHighlight = poi.type === 'highlight' && !poi.place_id;
  const displayName = poi.name || poi.title;
  const displayDescription = poi.description;

  const stopNumber = poi.id?.startsWith?.('regular-')
    ? parseInt(poi.id.replace('regular-', ''), 10) + 1
    : null;

  const matchedWaypoint = waypoints.find(wp => {
    if (!wp) return false;
    const poiName = (poi.name || poi.title || '').toLowerCase();
    const wpName = (wp.name || '').toLowerCase();
    return (poiName && wpName && poiName === wpName)
      || (poi.place_id && wp.place_id && poi.place_id === wp.place_id);
  });
  // Only show curator tip if it's genuinely different from the main description
  const rawTip = matchedWaypoint?.description || '';
  const curatorTip = rawTip && rawTip !== displayDescription ? rawTip : '';

  // Live details
  const openingHours = placeDetails?.opening_hours;
  const isOpenNow = openingHours?.isOpen?.();
  const todayHours = (() => {
    if (!openingHours?.weekday_text) return null;
    const dayIndex = new Date().getDay();
    const adjusted = dayIndex === 0 ? 6 : dayIndex - 1;
    const raw = openingHours.weekday_text[adjusted] || null;
    // Strip the day name prefix (e.g. "Monday: 6:00 AM – 6:00 PM" → "6:00 AM – 6:00 PM")
    return raw ? raw.replace(/^\w+:\s*/, '') : null;
  })();
  const weeklyHours = openingHours?.weekday_text || [];

  const phone = placeDetails?.formatted_phone_number || poi.phone;
  const website = placeDetails?.website || poi.website;
  const googleMapsLink = placeDetails?.url;
  const placePhoto = (() => {
    try {
      const photos = placeDetails?.photos;
      if (photos && photos.length > 0) {
        return photos[0].getUrl({ maxWidth: 600, maxHeight: 300 });
      }
    } catch {}
    return null;
  })();

  const handleGetDirections = () => {
    const lat = poi.coordinates?.lat ?? poi.coords?.lat;
    const lng = poi.coordinates?.lng ?? poi.coords?.lng;
    if (lat && lng) {
      window.open(
        `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=walking`,
        '_blank'
      );
    } else if (poi.address || poi.name || poi.title) {
      const dest = encodeURIComponent(poi.address || poi.name || poi.title);
      window.open(
        `https://www.google.com/maps/dir/?api=1&destination=${dest}&travelmode=walking`,
        '_blank'
      );
    } else if (googleMapsLink) {
      window.open(googleMapsLink, '_blank');
    }
  };

  const getCategoryLabel = (category) => {
    const labels = {
      food: 'Food & Drink', art: 'Art', architecture: 'Architecture',
      business: 'Local Business', museum: 'Museum', viewpoint: 'Viewpoint',
      cultural_venue: 'Cultural Venue', hidden_gem: 'Hidden Gem', shop: 'Shop',
    };
    return labels[category] || 'Point of Interest';
  };

  const hasDirections = !!(
    (poi.coordinates?.lat && poi.coordinates?.lng) ||
    (poi.coords?.lat && poi.coords?.lng) ||
    poi.address || poi.name || poi.title ||
    googleMapsLink
  );

  const hasPracticalInfo = !isRegularHighlight && (todayHours || poi.estimated_visit_duration || poi.price_range || phone || website);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-[2px]" />

      <div
        className="relative bg-white rounded-2xl max-w-xl w-full max-h-[90vh] overflow-hidden shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Gradient header ──────────────────────────── */}
        <div className="relative bg-gradient-to-r from-blue-600 via-purple-600 to-blue-700 px-7 pt-6 pb-5">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/15 hover:bg-white/25 transition-colors"
          >
            <X className="h-4 w-4 text-white" />
          </button>

          {/* Badge row — no rating here, moved to body */}
          <div className="flex items-center gap-2 mb-3">
            {isRegularHighlight ? (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold bg-purple-500/30 text-white border border-white/20">
                <MapPin className="w-3 h-3" />
                Walk Highlight
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold bg-amber-400/20 text-amber-200 border border-amber-300/30">
                <Sparkles className="w-3 h-3" />
                {getCategoryLabel(poi.category)}
              </span>
            )}
          </div>

          <h2 className="text-2xl font-bold text-white leading-tight pr-8">{displayName}</h2>

          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2">
            {stopNumber && (
              <span className="text-white/50 text-xs font-medium">
                Stop {stopNumber}{totalStops > 0 ? ` of ${totalStops}` : ''}
              </span>
            )}
            {poi.address && (
              <span className="text-white/50 text-xs flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                {poi.address}
              </span>
            )}
          </div>
        </div>

        {/* Place photo banner */}
        {placePhoto && (
          <img
            src={placePhoto}
            alt={displayName}
            className="w-full h-[180px] object-cover"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        )}

        {/* ── Body ─────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto px-7 py-6 space-y-6">

          {/* Rating + open/closed row — moved from header */}
          {!isRegularHighlight && (poi.user_ratings >= 4.0 || (openingHours && typeof isOpenNow === 'boolean')) && (
            <div className="flex items-center gap-4">
              {poi.user_ratings >= 4.0 && (
                <div className="flex items-center gap-1.5">
                  <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                  <span className="text-sm font-semibold text-gray-900">{poi.user_ratings.toFixed(1)}</span>
                  <span className="text-xs text-gray-400">rating</span>
                </div>
              )}
              {openingHours && typeof isOpenNow === 'boolean' && (
                <div className="flex items-center gap-1.5">
                  <span className={`w-2 h-2 rounded-full ${isOpenNow ? 'bg-green-500' : 'bg-red-500'}`} />
                  <span className={`text-sm font-medium ${isOpenNow ? 'text-green-700' : 'text-red-600'}`}>
                    {isOpenNow ? 'Open now' : 'Closed'}
                  </span>
                  {todayHours && (
                    <span className="text-sm text-gray-500">&middot; Today {todayHours}</span>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Description */}
          {displayDescription && (
            <p className="text-gray-600 text-sm leading-relaxed">{displayDescription}</p>
          )}

          {/* Curator tip */}
          {curatorTip && (
            <div className="bg-amber-50 border border-amber-100 rounded-xl px-4 py-3">
              <p className="text-amber-900 text-sm leading-relaxed">
                <span className="font-semibold">Local tip —</span> {curatorTip}
              </p>
            </div>
          )}

          {/* Cultural Significance */}
          {poi.cultural_significance && (
            <div className="bg-blue-50/70 rounded-xl px-4 py-3">
              <h4 className="font-semibold text-blue-900 text-sm mb-1">Why it matters</h4>
              <p className="text-blue-800 text-sm leading-relaxed">{poi.cultural_significance}</p>
            </div>
          )}

          {/* Insider Tips */}
          {poi.insider_tips && (
            <div className="bg-emerald-50/70 rounded-xl px-4 py-3">
              <h4 className="font-semibold text-emerald-900 text-sm mb-1">Insider tip</h4>
              <p className="text-emerald-800 text-sm leading-relaxed">{poi.insider_tips}</p>
            </div>
          )}

          {/* Practical Info */}
          {hasPracticalInfo && (
            <div className="space-y-2.5">
              {/* Hours — expandable weekly schedule */}
              {todayHours && (
                <div className="bg-gray-50 rounded-xl overflow-hidden">
                  <button
                    type="button"
                    onClick={() => weeklyHours.length > 0 && setShowFullHours(!showFullHours)}
                    className="w-full flex items-center gap-3 p-3 text-left hover:bg-gray-100/50 transition-colors"
                  >
                    <Clock className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] text-gray-500 uppercase tracking-wider font-medium">Today</span>
                        {openingHours && typeof isOpenNow === 'boolean' && (
                          <span className={`w-1.5 h-1.5 rounded-full ${isOpenNow ? 'bg-green-500' : 'bg-red-500'}`} />
                        )}
                      </div>
                      <p className="text-sm text-gray-700">{todayHours}</p>
                    </div>
                    {weeklyHours.length > 0 && (
                      showFullHours
                        ? <ChevronUp className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        : <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    )}
                  </button>
                  {showFullHours && weeklyHours.length > 0 && (
                    <div className="px-3 pb-3 pt-0 border-t border-gray-100">
                      {weeklyHours.map((line, i) => (
                        <p key={i} className="text-xs text-gray-600 py-0.5">{line}</p>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {poi.estimated_visit_duration && (
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                    <Clock className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <div>
                      <p className="text-[11px] text-gray-500 uppercase tracking-wider font-medium">Duration</p>
                      <p className="text-sm text-gray-700">{poi.estimated_visit_duration} min</p>
                    </div>
                  </div>
                )}
                {poi.price_range && (
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                    <DollarSign className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <div>
                      <p className="text-[11px] text-gray-500 uppercase tracking-wider font-medium">Price</p>
                      <p className="text-sm text-gray-700">{poi.price_range}</p>
                    </div>
                  </div>
                )}
                {phone && (
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                    <Phone className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <a href={`tel:${phone}`} className="text-sm text-blue-600 hover:underline">{phone}</a>
                  </div>
                )}
                {website && (
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                    <Globe className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <a
                      href={website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1"
                    >
                      Visit Website
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Tags */}
          {poi.tags && poi.tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {poi.tags.map((tag, i) => (
                <button
                  key={i}
                  onClick={() => onTagClick(tag)}
                  className="px-2.5 py-1 bg-gray-100 hover:bg-blue-100 text-gray-500 hover:text-blue-600 text-xs rounded-lg transition-colors"
                >
                  #{tag}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* ── Footer actions ───────────────────────────── */}
        <div className="px-7 py-4 border-t border-gray-100 bg-gray-50/50 flex items-center gap-3">
          <button
            onClick={handleGetDirections}
            disabled={!hasDirections}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-bold bg-amber-500 hover:bg-amber-600 active:bg-amber-700 text-white shadow-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Navigation className="h-4 w-4" />
            Get Walking Directions
          </button>

          {googleMapsLink && (
            <a
              href={googleMapsLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-gray-600 border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-colors"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              Google Maps
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default POIDetailModal;
